import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CONTACT } from "../lib/data";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      `Siddharth Offsets ("we", "us", "our") operates an offset printing business located at ${CONTACT.address}. This Privacy Policy explains how we collect, use and protect your information when you visit our website or contact us.`,
    ],
  },
  {
    title: "2. Information we collect",
    body: [
      "Enquiry form data: when you submit our enquiry form we collect your name, phone number, email address, the service type and quantity you are interested in, and any message you provide.",
      "Communication data: if you contact us via WhatsApp or email, we receive the information you choose to share with us through those channels.",
      "Usage & advertising data: we use cookies and similar technologies, including the Meta (Facebook) Pixel, which collects information such as your IP address, browser information, pages visited and actions taken on this site (for example page views, contact button clicks and form submissions).",
    ],
  },
  {
    title: "3. How we use your information",
    body: [
      "To respond to your enquiries and prepare printing quotations.",
      "To communicate with you about your print job via phone, email or WhatsApp.",
      "To measure and improve the effectiveness of our advertising, including Facebook/Instagram ads, using the Meta Pixel.",
      "We do not sell your personal information to anyone.",
    ],
  },
  {
    title: "4. Sharing of information",
    body: [
      "Email delivery providers: enquiry details are emailed to us through a transactional email service (Resend) for the sole purpose of delivering your enquiry to our inbox.",
      "Meta Platforms: through the Meta Pixel, certain usage events are shared with Meta Platforms, Inc. to help us run and measure advertising. Meta's use of this data is governed by the Meta Privacy Policy (https://www.facebook.com/privacy/policy/).",
      "WhatsApp: if you choose to contact us on WhatsApp, your communication is handled by WhatsApp/Meta under their own terms and privacy policy.",
      "We may disclose information if required to do so by law.",
    ],
  },
  {
    title: "5. Cookies & tracking",
    body: [
      "This website uses cookies and similar technologies for analytics and advertising measurement (including the Meta Pixel). You can control or delete cookies through your browser settings, and you can opt out of personalised Facebook ads through your Facebook Ad Preferences (https://www.facebook.com/adpreferences).",
    ],
  },
  {
    title: "6. Data retention",
    body: [
      "We retain enquiry details for as long as necessary to respond to your enquiry, fulfil your print orders and maintain business records, after which they are deleted or anonymised.",
    ],
  },
  {
    title: "7. Data security",
    body: [
      "We take reasonable technical and organisational measures to protect your information. However, no method of transmission over the internet is 100% secure and we cannot guarantee absolute security.",
    ],
  },
  {
    title: "8. Your rights",
    body: [
      `You may request access to, correction of, or deletion of your personal information at any time by emailing us at ${CONTACT.email} or messaging us on WhatsApp at ${CONTACT.whatsapp}. We will respond within a reasonable timeframe.`,
    ],
  },
  {
    title: "9. Children's privacy",
    body: [
      "Our website and services are intended for businesses and adults. We do not knowingly collect personal information from children under 18.",
    ],
  },
  {
    title: "10. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. The latest version will always be available on this page with the effective date below.",
    ],
  },
  {
    title: "11. Contact us",
    body: [
      `Siddharth Offsets — ${CONTACT.address}. Email: ${CONTACT.email}. WhatsApp: ${CONTACT.whatsapp}.`,
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="bg-[#F4F4F0] text-[#0E0E0E] min-h-screen" data-testid="privacy-policy-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F4F4F0]/85 backdrop-blur-md border-b border-black">
        <div className="flex items-center justify-between px-4 sm:px-6 md:px-12 h-16">
          <Link to="/" data-testid="privacy-logo-link" className="font-display font-bold text-base sm:text-lg tracking-tight uppercase">
            Siddharth Offsets<span className="text-[#FF3B30]">®</span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-black px-4 py-2 hover:bg-black hover:text-[#F4F4F0] transition-colors duration-200"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
        <div className="flex h-1.5" aria-hidden="true">
          <span className="flex-1 bg-[#00AEEF]" />
          <span className="flex-1 bg-[#EC008C]" />
          <span className="flex-1 bg-[#FFD200]" />
          <span className="flex-1 bg-[#0E0E0E]" />
        </div>
      </nav>

      <main className="px-6 md:px-12 lg:px-24 pt-32 pb-24 max-w-4xl">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-black/60 mb-4">( Legal )</p>
        <h1 className="font-display font-bold uppercase tracking-tight leading-none text-4xl sm:text-6xl mb-4">
          Privacy <span className="text-[#FF3B30]">Policy</span>
        </h1>
        <p className="font-mono text-xs uppercase tracking-widest text-black/50 mb-14">Effective date: 1 June 2026</p>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title} className="border-t border-black pt-6">
              <h2 className="font-display font-semibold uppercase text-xl sm:text-2xl tracking-tight mb-4">{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="font-mono text-sm leading-relaxed text-black/80 mb-3">{p}</p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <footer className="bg-[#0E0E0E] text-[#F4F4F0] px-6 md:px-12 lg:px-24 py-10 font-mono text-xs flex flex-col md:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} Siddharth Offsets, Indore</span>
        <Link to="/" className="hover:text-[#FF3B30] transition-colors duration-200">siddharthoffsets.net</Link>
      </footer>
    </div>
  );
}
